import { Icon } from "@iconify/react";
import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useApi from "../hook/useApi";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import Loader from "./Loader/Loader";
import { ValidateInputs } from "../utils/helper";

const SignInLayer = () => {
  const { AsyncPostApiCall } = useApi();
  const { login: setAuthToken, isAuthenticated, subAdminId } = useAuth();
  const navigate = useNavigate();
  const { branding, isMaster } = useBranding();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    loginId: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [passwordVisible, setPasswordVisible] = useState(false);
  const location = useLocation();
  const isSubAdminLogin = location.pathname !== "/admin-login" && location.pathname !== "/admin-login/" && location.pathname === "/";
  const theme = sessionStorage.getItem('theme')
  const isDark = theme === 'dark'

  // Removed automatic redirect to allow users to switch accounts or log in as sub-admin from the login page.
  // useEffect(() => {
  //   if (isAuthenticated) {
  //     navigate("/dashboard");
  //   }
  // }, [isAuthenticated, navigate]);

  console.log('branding', branding)

  const appName = branding?.appName || "ProTrade";

  const handleValues = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    const updatedInputs = ValidateInputs(name, value, errors);
    setErrors(updatedInputs);
  };

  useEffect(() => {
    document.title = isSubAdminLogin
      ? `${appName} - Sub-Admin`
      : isMaster ? `${appName} - Super-Admin` : `${appName} - Admin`;
  }, [isSubAdminLogin, appName, isMaster]);

  const validate = () => {
    const newErrors = {};

    if (isSubAdminLogin) {
      if (!formData?.loginId?.trim()) {
        newErrors.loginId = "Please enter your login ID";
      } else if (formData.loginId.trim().length > 64) {
        newErrors.loginId = "Login ID is too long";
      }
      if (!formData.password?.trim()) {
        newErrors.password = "Password is required";
      }

      else if (formData.password.length > 128) {
        newErrors.password = "Password cannot exceed 128 characters";
      }
    } else {
      if (!formData?.email?.trim()) {
        newErrors.email = "Please enter an email address";
      } else if (formData.email.trim().length > 100) {
        newErrors.email = "Email cannot exceed 100 characters";
      } else if (
        !/^[a-zA-Z0-9]+(?:\.[a-zA-Z0-9]+)*@[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/.test(
          formData.email,
        )
      ) {
        newErrors.email = "Please enter a valid email address";
      }
      if (!formData.password.trim()) {
        newErrors.password = "Password is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const login = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }
    setLoading(true);
    let url;
    let payload;

    if (isSubAdminLogin) {
      url = "/auth/subadmin-login";
      payload = {
        loginId: formData.loginId.trim(),
        password: formData.password,
      };
    } else {
      url = "/auth/admin-login";
      payload = { email: formData?.email, password: formData?.password };
    }

    const result = await AsyncPostApiCall(url, payload);

    console.log('result', result)

    if (result?.errors) {
      toast.error(
        Array.isArray(result.errors) ? result.errors[0] : result.errors,
      );
    } else if (result?.success && result?.access_token) {
      if (isSubAdminLogin) {
        setAuthToken(
          result?.access_token,
          result?.subadmin,
          result?.subadmin?.subAdminId,
          result?.sessionSecret,
        );
        toast.success(result.msg || "Login successful");
        navigate("/dashboard");
      } else {
        setAuthToken(
          result.access_token,
          result?.admin,
          result?.admin?.subAdminId,
        );
        toast.success(result?.message || "You have successfully logged in.");

        navigate("/dashboard");
      }
      setErrors({});
    } else {
      toast.error(result?.msg || result?.message || "Login failed");
    }
    setLoading(false);
  };

  return (
    <>
      {loading && <Loader />}

      <section className="auth bg-base d-flex flex-wrap">
        <div className="auth-left d-lg-block d-none">
          <div className="d-flex align-items-center flex-column h-100 justify-content-center">
            <img src="assets/images/signup.svg" alt="Login Visual" />
          </div>
        </div>
        <div className="auth-right py-32 px-24 d-flex flex-column justify-content-center">
          <div className="max-w-464-px mx-auto w-100">
            <div>
              <Link to="/" className="mb-40 max-w-290-px d-block">
                <img src={branding?.standardLogoUrl || "assets/images/logo.svg"} alt={`${appName} Logo`} style={{ maxHeight: "60px", objectFit: "contain" }} />
              </Link>
              <h4 className="mb-12">
                Sign In to your{" "}
                {isSubAdminLogin
                  ? "Sub-Admin"
                  : "Super-Admin"}{" "}
                Account
              </h4>
              <p className="mb-32 text-secondary-light text-lg">
                Welcome back! Please enter your details
              </p>
            </div>

            {isAuthenticated && isSubAdminLogin && subAdminId && (
              <div className="alert alert-primary d-flex justify-content-between align-items-center mb-24 px-16 py-12 radius-8">
                <span className="text-primary-600 fw-medium">You are currently logged in.</span>
                <Link to="/dashboard" className="btn btn-primary btn-sm radius-8">
                  Go to Dashboard
                </Link>
              </div>
            )}

            {isAuthenticated && !isSubAdminLogin && !subAdminId && (
              <div className="alert alert-primary d-flex justify-content-between align-items-center mb-24 px-16 py-12 radius-8">
                <span className="text-primary-600 fw-medium">You are currently logged in.</span>
                <Link to="/dashboard" className="btn btn-primary btn-sm radius-8">
                  Go to Dashboard
                </Link>
              </div>
            )}

            <form onSubmit={login}>
              {isSubAdminLogin ? (
                <div className="icon-field mb-16">
                  <span className="icon top-50 translate-middle-y">
                    <Icon icon="solar:user-id-bold" />
                  </span>
                  <input
                    name="loginId"
                    type="text"
                    className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.loginId ? "is-invalid" : ""
                      }`}
                    placeholder="Login ID"
                    value={formData?.loginId}
                    onChange={handleValues}
                    autoComplete="username"
                  />
                  {errors.loginId && (
                    <div className="invalid-feedback">{errors.loginId}</div>
                  )}
                </div>
              ) : (
                <div className="icon-field mb-16">
                  <span className="icon top-50 translate-middle-y">
                    <Icon icon="mage:email" />
                  </span>
                  <input
                    name="email"
                    type="email"
                    className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.email ? "is-invalid" : ""
                      }`}
                    placeholder="Email"
                    value={formData?.email}
                    onChange={handleValues}
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>
              )}

              <div className="position-relative mb-20">
                <div className="icon-field" style={{ display: "flex" }}>
                  <span className="icon top-50 translate-middle-y">
                    <Icon icon="solar:lock-password-outline" />
                  </span>
                  <input
                    name="password"
                    type={passwordVisible ? "text" : "password"}
                    className={`form-control h-56-px bg-neutral-50 radius-12 ${errors.password ? "is-invalid" : ""
                      }`}
                    id="your-password"
                    placeholder="Password"
                    value={formData?.password}
                    onChange={handleValues}
                  />
                  <span
                    type="button"
                    className="icon password-eye-icon top-50 translate-middle-y"
                    style={{
                      position: "absolute",
                      left: "auto",
                      right: `${errors.password ? "1em" : "0"} `,
                    }}
                    onClick={() => setPasswordVisible(!passwordVisible)}
                  >
                    <Icon
                      icon={
                        passwordVisible
                          ? "solar:eye-closed-bold"
                          : "solar:eye-bold"
                      }
                    />
                  </span>
                </div>
                {errors.password && (
                  <div className="invalid-feedback d-block">
                    {errors.password}
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-between gap-2">
                {!isSubAdminLogin && (
                  <Link
                    to="/forgot-password"
                    className="text-primary-600 fw-medium"
                  >
                    Forgot Password?
                  </Link>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12 mt-32"
              >
                Sign In
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default SignInLayer;
